import React from 'react';

interface SolutionFlowVisualizationProps {
  flowText: string;
  className?: string;
}

interface FlowStep {
  number: number;
  text: string;
}

const SolutionFlowVisualization: React.FC<SolutionFlowVisualizationProps> = ({ 
  flowText, 
  className = '' 
}) => {

  // Parse the flow text to extract numbered steps
  const parseFlowSteps = (text: string): FlowStep[] => {
    const steps: FlowStep[] = [];
    
    // Split by newlines and match patterns like "1. Text" or "1) Text"
    const lines = text.split('\n');
    
    for (const line of lines) {
      const match = line.match(/^(\d+)[.)]\s*(.+)$/);
      if (match) {
        const number = Number.parseInt(match[1], 10);
        const stepText = match[2].trim();
        
        if (stepText) {
          steps.push({ number, text: stepText });
        }
      }
    }
    
    return steps;
  };

  const steps = parseFlowSteps(flowText);

  if (steps.length === 0) {
    // Fallback to plain text if parsing fails
    return (
      <div className={`text-gray-700 leading-relaxed whitespace-pre-line ${className}`}>
        {flowText}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Desktop View - Grid Layout */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
        {steps.map((step, index) => {
          const isLastInRow = (index + 1) % 3 === 0; // for lg screens
          const isLastInRowMd = (index + 1) % 2 === 0; // for md screens
          const isLastItem = index === steps.length - 1;
          
          return (
            <div key={step.number} className="relative">
              {/* Card with number inside */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-colors duration-200 hover:bg-blue-50 hover:border-blue-200 min-h-[100px] flex flex-col">
                <div className="w-7 h-7 rounded-full flex items-center justify-center mb-3 font-semibold text-xs bg-blue-600 text-white flex-shrink-0">
                  {step.number}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {step.text}
                </p>
              </div>

              {/* Arrow to the right (only if not last in row and not last item) */}
              {!isLastItem && !isLastInRow && (
                <div className="hidden lg:flex absolute top-1/2 -right-6 transform -translate-y-1/2 text-blue-400">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              
              {/* Arrow to the right for md screens */}
              {!isLastItem && !isLastInRowMd && (
                <div className="lg:hidden flex absolute top-1/2 -right-6 transform -translate-y-1/2 text-blue-400">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile View - Vertical List */}
      <div className="md:hidden space-y-3">
        {steps.map((step, index) => (
          <div key={step.number}>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-colors duration-200 hover:bg-blue-50 hover:border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs bg-blue-600 text-white">
                  {step.number}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed pt-0.5">
                  {step.text}
                </p>
              </div>
            </div>

            {/* Down Arrow */}
            {index < steps.length - 1 && (
              <div className="flex justify-center py-1">
                <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SolutionFlowVisualization;
